import { Button, Input, Select } from "antd";
import { TableProps } from "antd/es/table";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import FilterIcon from "../../../assets/icons/FilterIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SettingIcon from "../../../assets/icons/SettingIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
// import CustomReportRequestButtonModal from "../../../components/Attendance/CustomAttendance/CustomReportRequestButtonModal";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import CustomReportRequestButtonModal from "../../../components/Attendance/CustomAttendance/CustomReportRequestButtonModal";
import FilterModal from "../../../components/Attendance/CustomAttendance/FilterModal";
import CustomTable from "../../../components/ui/CustomTable";
import IconButton from "../../../components/ui/IconButton";
import { employeeSearchTypeList } from "../../../constants/constants";
import { useGetEmployeesQuery } from "../../../features/employee/employeeApi";
import { setAttendanceFilter } from "../../../features/root/rootSlice";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useExcelDownload from "../../../hooks/useExcelDownload";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { EmployeeData, GetEmployeeParamsType } from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import { replaceAllWithUndefined } from "../../../utils/obj";
import {
  addKeyInArray,
  selectSearchOption,
  skipData,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import DownloadCustomAttenModal from "./DownloadCustomAttenModal";

dayjs.extend(customParseFormat);

type ModalType = "FILTER" | "REPORT_REQUEST";

type GetEmployeeTypeParams = {
  attendanceType?: "remote" | "location" | "wifi";
} & GetEmployeeParamsType;

export type CustomAttendanceFilterType = Omit<
  GetEmployeeTypeParams,
  "limit" | "skip"
>;

const searchTypeSelect = employeeSearchTypeList?.map((item) => ({
  label: underscoreToCapitalizeString(item),
  value: item,
}));

export default function CustomAttendance() {
  const location = useLocation();
  const { keyword, inputValue, handleInputChange, handleInputSearch } =
    useDebounce();
  const { authInfo } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { attendanceFilter } = useAppSelector((state) => state.root);
  const { page, limit, handlePagination, handleLimit, resetPagination } =
    useTable();
  const { open, handleClose, handleOpen } = useModal<undefined, ModalType>();
  const [loading, setLoading] = useState(false);

  const params: GetEmployeeTypeParams = {
    limit,
    skip: skipData(page, limit),
    status: "active",
    manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
    ...replaceAllWithUndefined(attendanceFilter),
    search: keyword || undefined,
    is_location_active:
      attendanceFilter?.attendanceType === "location" ? 1 : undefined,
    is_remote_active:
      attendanceFilter?.attendanceType === "remote" ? 1 : undefined,
    is_wifi_network_active:
      attendanceFilter?.attendanceType === "wifi" ? 1 : undefined,
    attendanceType: undefined,
  };

  console.log(attendanceFilter);

  const { isLoading, isError, error, data } = useGetEmployeesQuery({
    params,
  });
  useError(isError, error, "Get employee data fail");

  const { excelLoading, handleExcelLoading, downloadExcel } = useExcelDownload(
    "Custom_attendance_report"
  );

  const handleFilter = <K extends keyof CustomAttendanceFilterType>(
    filterType: K,
    value: CustomAttendanceFilterType[K]
  ) => {
    // setFilter((prev) => ({
    //   ...prev,
    //   [filterType]: value,
    // }));

    dispatch(setAttendanceFilter({ [filterType]: value }));
  };

  const columns: TableProps<EmployeeData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee Id",
      dataIndex: "employee_id",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Employee Name",
      dataIndex: "member",
      render: (val) => val?.profile?.name ?? "n/a",
    },
    {
      title: "Department",
      dataIndex: "role",
      render: (val) => val?.department?.name ?? "n/a",
    },
    {
      title: "Designation",
      dataIndex: "role",
      render: (val) => val?.name ?? "n/a",
    },
    {
      title: "Attendance Type",
      // align: "center",
      render: (val) => {
        const isEmpty =
          !val?.is_wifi_network_active &&
          !val?.is_remote_active &&
          !val?.is_location_active;

        return (
          <div className="inline-flex items-center gap-1 min-w-[118px]">
            {isEmpty ? "-" : null}
            {val?.is_wifi_network_active ? (
              <span className="tooltip-box !cursor-default">W</span>
            ) : null}{" "}
            {val?.is_remote_active ? (
              <span className="tooltip-box !cursor-default">H</span>
            ) : null}
            {val?.is_location_active ? (
              <span className="tooltip-box !cursor-default">G</span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val: string) => (
        <Link to={`${location.pathname}/view/${val}`}>
          <Button
            className="download-button !p-0 !h-fit !border-none !shadow-none"
            size="small"
            icon={<ViewIcon />}
          >
            View
          </Button>
        </Link>
      ),
    },
  ];

  useEffect(() => {
    resetPagination();
    handleFilter("search", keyword || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, resetPagination]);

  useEffect(() => {
    if (attendanceFilter?.search) {
      handleInputSearch(attendanceFilter?.search);
      setLoading(true);

      setTimeout(() => {
        setLoading(false);
      }, 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceFilter?.search]);

  // const handleDownload = () => {
  //   downloadXlsx(
  //     EMPLOYEE_MANAGE_API + API_URL.getEmployees,
  //     { ...filter, skip: 0, limit: 0 },
  //     "Custom_attendance_report",
  //     "employees",
  //     (custom_attendance: GetEmployeesResponseType["data"]["employees"][0]) => {
  //       return {
  //         "Employee ID": custom_attendance?.employee_id ?? "-",
  //         "Employee Name": custom_attendance?.member?.profile?.name ?? "-",
  //         "Employee Email": custom_attendance?.member?.profile?.email ?? "-",
  //         "Employee Department": custom_attendance?.department ?? "-",
  //         "Employee Designation": custom_attendance?.designation ?? "-",
  //         "Employee Line Manager": "-",
  //         "Line Manager Email": "-",
  //         "Employee Address": "-",
  //         "Working Days": "-",
  //         Present: "-",
  //         "On time": "-",
  //         Late: "-",
  //         "Left Timely": "-",
  //         "Left early": "-",
  //         "On leave": "-",
  //         Absent: "-",
  //         "Total Hours": "-",
  //         Overtime: "-",
  //         "Total Remote Checkin": "-",
  //         "Total Office Checkin": "-",
  //         "Total Remote Checkout": "-",
  //         "Total Office Checkout": "-",
  //         "Total Checkout Missing": "-",
  //         "Joining Prorated": "-",
  //         "Leave Days": "-",
  //         "Late Days": "-",
  //         "Absent Days": "-",
  //       };
  //     }
  //   );
  // };

  return (
    <SidebarLayout title="Custom Attendance">
      <CustomTable
        loading={isLoading || loading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.totalLength}
        dataSource={addKeyInArray(data?.data?.employees)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Select
              showSearch
              placeholder="Search Type"
              value={attendanceFilter?.search_type}
              options={searchTypeSelect}
              onChange={(val) => handleFilter("search_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[120px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Input
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                attendanceFilter?.search_type || "name",
                true
              )}`}
              style={{ maxWidth: 180 }}
              value={inputValue}
              onChange={handleInputChange}
            />
            <IconButton
              onClick={() =>
                handleOpen({
                  type: "FILTER",
                })
              }
              className="normal-button"
              nextIcon={<FilterIcon />}
            >
              Filter
            </IconButton>

            <Button
              onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
              className="download-button"
              icon={<DownloadIcon />}
            >
              Report Requests
            </Button>
            <Link to="/admin/settings/office-settings/attendance">
              <Button
                type="primary"
                className="middle-button"
                icon={<SettingIcon />}
              />
            </Link>
          </>
        }
      />

      {open.type === "FILTER" && (
        <FilterModal
          open={open.open}
          handleClose={handleClose}
          filter={attendanceFilter}
          setFilter={(values) => {
            dispatch(setAttendanceFilter(values));
            resetPagination();
          }}
        />
      )}

      {open.type === "VIEW" && (
        <DownloadCustomAttenModal
          open={open.open}
          handleClose={handleClose}
          loading={excelLoading}
          handleExcelLoading={handleExcelLoading}
          downloadExcel={downloadExcel}
          totalLength={data?.data?.totalLength || 1}
        />
      )}

      {open.type === "REPORT_REQUEST" && (
        <CustomReportRequestButtonModal
          open={open.open}
          handleClose={handleClose}
          filter={{
            ...attendanceFilter,
            search: keyword || undefined,
            skip: undefined,
            limit: undefined,
          }}
        />
      )}

      {/* {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          handleSubmit={(params: any) => {
            generateCustomAttendanceReport({
              body: {
                name: "Custom Attendance",
              },
              params,
            });
          }}
        />
      )} */}
    </SidebarLayout>
  );
}
